import React, { useEffect, useState } from "react";
import { useMutation } from "react-query";
import { useSelector } from "react-redux";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Textarea,
  Tooltip,
  Typography,
} from "@material-tailwind/react";
import { FaEdit, FaTrash, FaCheck, FaArrowLeft } from "react-icons/fa";
import { httpMushPro } from "../../../services/http-common";
import { toast } from "react-toastify";
import { formatFullTime } from "../../../utils/dateFormat";

const Notes = () => {
  const { selectedSeekerDetails } = useSelector((state) => state.dataFlow);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedNote, setSelectedNote] = useState(null);
  const [isAddingNote, setIsAddingNote] = useState(false);
  const [allNotes, setAllNotes] = useState(null);
  const [newNote, setNewNote] = useState("");

  const getSeekerNotes = useMutation(
    () => {
      return httpMushPro.post("user/seeker-note/get-notes", {
        seekerId: selectedSeekerDetails?.attendees?.user?.id,
      });
    },
    {
      onSuccess: ({ data }) => {
        setAllNotes(data.seekerNotes);
      },
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const addNewNote = useMutation(
    (note) => {
      return httpMushPro.post("user/seeker-note/create", {
        seekerId: selectedSeekerDetails?.attendees?.user?.id,
        description: note.note,
      });
    },
    {
      onSuccess: () => {
        toast.success("Note added successfully");
        getSeekerNotes.mutate();
        setIsAddingNote(false);
      },
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const updateNote = useMutation(
    () => {
      return httpMushPro.put("user/seeker-note/update", {
        id: selectedNote.id,
        description: selectedNote.description,
      });
    },
    {
      onSuccess: () => {
        toast.success("Note updated successfully");
        getSeekerNotes.mutate();
        setIsEditing(false);
      },
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const deleteNote = useMutation(
    (id) => {
      return httpMushPro.delete(`user/seeker-note/delete?id=${id}`, null);
    },
    {
      onSuccess: () => {
        getSeekerNotes.mutate();
        toast.success("Successfully Deleted");
      },
      onError: (error) => {
        toast.error(error.response.data.message);
      },
    }
  );

  const handleEditClick = (note) => {
    setIsEditing(true);
    setSelectedNote(note);
  };

  const handleAddNote = (e) => {
    e.preventDefault();
    if (newNote.trim()) {
      addNewNote.mutate({ note: newNote });
      setNewNote("");
    }
  };

  const handleDeleteNote = (noteId) => {
    deleteNote.mutate(noteId);
  };

  const handleShowAdd = () => {
    setIsAddingNote(!isAddingNote);
  };

  const handleUpdateNote = () => {
    updateNote.mutate();
  };

  useEffect(() => {
    selectedSeekerDetails && getSeekerNotes.mutate();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      {isAddingNote ? (
        <Card className="w-full ">
          <CardBody>
            <div className="mb-3 flex items-center justify-start">
              <FaArrowLeft
                className="mr-2 cursor-pointer"
                onClick={handleShowAdd}
              />
              <Typography variant="h5" color="blue-gray">
                Add New Note
              </Typography>
            </div>
            <form onSubmit={handleAddNote} className="flex flex-col gap-4">
              <Textarea
                label="New Note"
                value={newNote}
                onChange={(e) => setNewNote(e.target.value)}
                className="w-full"
              />
              <Button type="submit">Add Note</Button>
            </form>
          </CardBody>
        </Card>
      ) : (
        <Card className="w-full">
          <CardBody>
            <div className="mb-3 flex items-center justify-between">
              <Typography
                variant="h5"
                color="blue-gray"
                className="font-medium"
              >
                Notes for {selectedSeekerDetails?.attendees?.user?.firstName}
              </Typography>
              <Button
                className="flex items-center normal-case gap-1.5 font-normal bg-[#000080]"
                onClick={handleShowAdd}
              >
                + Add Note
              </Button>
            </div>
            <div className="divide-y divide-gray-200">
              <div className="flex flex-col gap-4">
                {allNotes &&
                  allNotes.map((note) => (
                    <div key={note.id} className="border-b pb-4">
                      {isEditing && selectedNote.id === note.id ? (
                        <Textarea
                          value={selectedNote.description}
                          onChange={(e) =>
                            setSelectedNote({
                              ...selectedNote,
                              description: e.target.value,
                            })
                          }
                          className="w-full"
                        />
                      ) : (
                        <p>
                          <strong>Note:</strong> {note.description}
                        </p>
                      )}
                      <div className="flex justify-between items-center mt-2">
                        <p className="text-gray-500 text-sm">
                          <i className="mdi mdi-calendar-clock"></i>{" "}
                          {formatFullTime(note.updated_at)}
                        </p>
                        <div className="flex gap-2">
                          {isEditing && selectedNote.id === note.id ? (
                            <FaCheck
                              onClick={handleUpdateNote}
                              className="cursor-pointer text-green-500"
                            />
                          ) : (
                            <FaEdit
                              onClick={() => handleEditClick(note)}
                              className="cursor-pointer text-blue-500"
                            />
                          )}
                          <FaTrash
                            onClick={() => handleDeleteNote(note.id)}
                            className="cursor-pointer text-red-500"
                          />
                        </div>
                      </div>
                    </div>
                  ))}
              </div>
            </div>
          </CardBody>
        </Card>
      )}
    </>
  );
};

export default Notes;
